/**
 * Fixes alphabetical sorting to ignore "Survival" at the beginning of product titles.
 * This ensures products are sorted correctly even though "Survival" is hidden from display.
 */
if (!customElements.get('product-sort-fix')) {
  class ProductSortFix extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      // Listen for when filters/sorting are updated
      document.addEventListener('on:facet-filters:updated', () => {
        setTimeout(() => this.handleProductsUpdate(), 50);
      });

      // Also check on initial page load
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          setTimeout(() => this.handleProductsUpdate(), 100);
        });
      } else {
        // Use setTimeout to ensure products are rendered
        setTimeout(() => this.handleProductsUpdate(), 100);
      }

      // Watch for URL changes (e.g., when sort_by parameter changes)
      this.observeUrlChanges();
    }

    /**
     * Observes URL changes to detect when sorting changes
     */
    observeUrlChanges() {
      let lastUrl = window.location.href;
      
      // Check URL periodically (for cases where URL changes without events)
      setInterval(() => {
        if (window.location.href !== lastUrl) {
          lastUrl = window.location.href;
          setTimeout(() => this.handleProductsUpdate(), 100);
        }
      }, 200);

      // Also listen to popstate (back/forward button)
      window.addEventListener('popstate', () => {
        setTimeout(() => this.handleProductsUpdate(), 100);
      });
    }

    /**
     * Handles product updates and re-sorts if needed
     */
    handleProductsUpdate() {
      const urlParams = new URLSearchParams(window.location.search);
      const sortBy = urlParams.get('sort_by') || 'manual';

      // Only apply fix for alphabetical sorting
      if (sortBy === 'title-ascending' || sortBy === 'title-descending') {
        this.sortProductsAlphabetically(sortBy === 'title-ascending');
      }
    }

    /**
     * Sorts products alphabetically, ignoring "Survival" at the beginning
     * @param {boolean} ascending - Whether to sort ascending (A-Z) or descending (Z-A)
     */
    sortProductsAlphabetically(ascending = true) {
      const productsGrid = document.querySelector('.main-products-grid__results ul');
      if (!productsGrid) return;

      const productItems = Array.from(productsGrid.querySelectorAll('.js-pagination-result'));
      
      if (productItems.length === 0) return;

      // Create array of items with their sort keys
      const itemsWithSortKey = productItems.map((item) => {
        // Try to get title from data attribute first (most reliable)
        const titleLink = item.querySelector('.card-link[data-product-title]');
        let title = '';
        
        if (titleLink) {
          title = titleLink.getAttribute('data-product-title') || '';
        } else {
          // Fallback: try to get from aria-label on image link
          const imageLink = item.querySelector('a[aria-label].js-prod-link');
          if (imageLink) {
            title = imageLink.getAttribute('aria-label') || '';
          } else {
            // Last resort: try to reconstruct from displayed title
            const cardLink = item.querySelector('.card-link');
            if (cardLink) {
              // If "Survival" was removed, we can't perfectly reconstruct, but we'll use what we have
              title = cardLink.textContent.trim();
            }
          }
        }

        // Remove "Survival" from the beginning (case-insensitive) for sorting
        let sortKey = title.trim();
        const survivalRegex = /^survival\s+/i;
        if (survivalRegex.test(sortKey)) {
          sortKey = sortKey.replace(survivalRegex, '').trim();
        }

        return {
          element: item,
          originalTitle: title,
          sortKey: sortKey || title // Fallback to original title if sortKey is empty
        };
      });

      // Sort by the sort key
      itemsWithSortKey.sort((a, b) => {
        const comparison = a.sortKey.localeCompare(b.sortKey, undefined, { 
          sensitivity: 'base',
          numeric: true 
        });
        return ascending ? comparison : -comparison;
      });

      // Reorder DOM elements (only if order actually changed)
      let orderChanged = false;
      itemsWithSortKey.forEach((item, index) => {
        const currentIndex = Array.from(productsGrid.children).indexOf(item.element);
        if (currentIndex !== index) {
          orderChanged = true;
        }
      });

      if (orderChanged) {
        const fragment = document.createDocumentFragment();
        itemsWithSortKey.forEach(({ element }) => {
          fragment.appendChild(element);
        });

        productsGrid.appendChild(fragment);
      }
    }
  }

  customElements.define('product-sort-fix', ProductSortFix);
}
